/**
 * Identity-hygiene — Zod schemas.
 *
 * Response shapes for /api/v1/tenants/{id}/identity/hygiene/* are
 * generated from the API contract; this module keeps the URL-state
 * and form schemas.
 */

import { z } from "zod";
import {
  dormantEntrySchema,
  dormantListSchema,
  hygieneSummarySchema,
  policyProposalSchema,
  policySchema,
  revokeLogEntrySchema,
  revokeLogListSchema,
} from "./api.generated";

export const stalenessToneSchema = z.enum([
  "fresh",
  "watch",
  "stale",
  "critical",
]);
export type StalenessTone = z.infer<typeof stalenessToneSchema>;

export const grantSourceSchema = z.enum(["direct", "group", "both"]);
export type GrantSource = z.infer<typeof grantSourceSchema>;

export const userStatusSchema = z.enum([
  "active",
  "inactive",
  "disabled",
  "locked",
  "deleted",
]);
export type UserStatus = z.infer<typeof userStatusSchema>;

// Response shapes are defined by the API contract and generated.
export {
  dormantEntrySchema,
  dormantListSchema,
  hygieneSummarySchema,
  policyProposalSchema,
  policySchema,
  revokeLogEntrySchema,
  revokeLogListSchema,
};
export type DormantEntry = z.infer<typeof dormantEntrySchema>;
export type HygieneSummary = z.infer<typeof hygieneSummarySchema>;
export type DormantList = z.infer<typeof dormantListSchema>;
export type Policy = z.infer<typeof policySchema>;
export type PolicyProposal = z.infer<typeof policyProposalSchema>;

// ── Auto-revoke policy form ──────────────────────────────────────────────

export const policyFormSchema = z.object({
  auto_revoke_enabled: z.boolean(),
  threshold_days: z.number().int().min(14).max(730),
  warn_days_before: z.number().int().min(0).max(30),
  max_revokes_per_tick: z.number().int().min(1).max(1000),
  requires_co_approval: z.boolean(),
});
export type PolicyForm = z.infer<typeof policyFormSchema>;

/**
 * Whether changing from `current` to `next` needs a second administrator:
 * automatic removal stays or turns on while either side requires
 * co-approval, or co-approval is relaxed. Mirrors the server's rule, which
 * refuses such a change applied at once; the console proposes it instead.
 */
export function needsCoApproval(current: PolicyForm, next: PolicyForm): boolean {
  if (next.auto_revoke_enabled && (current.requires_co_approval || next.requires_co_approval)) return true;
  return current.requires_co_approval && !next.requires_co_approval;
}

export function policySettings(p: PolicyForm): PolicyForm {
  return {
    auto_revoke_enabled: p.auto_revoke_enabled,
    threshold_days: p.threshold_days,
    warn_days_before: p.warn_days_before,
    max_revokes_per_tick: p.max_revokes_per_tick,
    requires_co_approval: p.requires_co_approval,
  };
}
export type RevokeLogEntry = z.infer<typeof revokeLogEntrySchema>;
export type RevokeLogList = z.infer<typeof revokeLogListSchema>;

// ── Certification campaigns ──────────────────────────────────────────────

// Campaign responses are defined by the API contract (CertificationCampaign)
// and generated into @/lib/certification-contract.
export { campaignScopeSchema, campaignSchema, campaignListSchema } from "@/lib/certification-contract";
export type Campaign = z.infer<typeof import("@/lib/certification-contract").campaignSchema>;
export type CampaignScope = z.infer<typeof import("@/lib/certification-contract").campaignScopeSchema>;
export type CampaignStatus = Campaign["status"];

export const itemDecisionSchema = z.enum([
  "pending",
  "approved",
  "revoked",
  "unknown",
  "needs_clarification",
  "resolved_external",
]);
export type ItemDecision = z.infer<typeof itemDecisionSchema>;

export { campaignItemSchema, campaignItemListSchema } from "@/lib/certification-contract";
export type CampaignItem = z.infer<typeof import("@/lib/certification-contract").campaignItemSchema>;

export const dormantFilterSearchSchema = z.object({
  section: z.enum(["dormant", "campaigns"]).optional(),
  tone: stalenessToneSchema.optional(),
  grant_source: grantSourceSchema.optional(),
  application_id: z.string().uuid().optional(),
  user_id: z.string().uuid().optional(),
  min_days_idle: z.coerce.number().int().min(0).max(365).optional(),
  include_fresh: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
export type DormantFilterSearch = z.infer<typeof dormantFilterSearchSchema>;
